import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("hk_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("hk_token");
    if (token && !user) {
      setLoading(true);
      api
        .get("/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("hk_user", JSON.stringify(r.data));
        })
        .catch(() => {
          localStorage.removeItem("hk_token");
          localStorage.removeItem("hk_user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const persist = (token, u) => {
    localStorage.setItem("hk_token", token);
    localStorage.setItem("hk_user", JSON.stringify(u));
    setUser(u);
  };

  const login = async (email, password, extra = {}) => {
    const { data } = await api.post("/auth/login", { email, password, ...extra });
    if (data.captcha_required) return { captcha_required: true };
    if (data.mfa_required) return { mfa_required: true };
    persist(data.token, data.user);
    return data.user;
  };

  const register = async (email, password, full_name, captcha, accept_terms, hp = {}) => {
    const { data } = await api.post("/auth/register", {
      email,
      password,
      full_name,
      accept_terms: !!accept_terms,
      captcha_token: captcha?.token,
      captcha_answer: captcha?.answer,
      ...hp,
    });
    persist(data.token, data.user);
    return data.user;
  };

  const telegramWidgetLogin = async (payload) => {
    const { data } = await api.post("/auth/telegram/widget", payload);
    persist(data.token, data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("hk_token");
    localStorage.removeItem("hk_user");
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isAdmin: user?.role === "admin",
      login,
      register,
      telegramWidgetLogin,
      logout,
      setUser,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
